import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  ExpandMore as ExpandMoreIcon,
  FormatListBulleted as ListIcon,
  PlaylistAdd as AddItemIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ContentCopy as CloneIcon
} from '@mui/icons-material';
import api from '../services/api';

const EvaluationFormManager = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_role: 'PROFESSIONAL',
    is_active: 0
  });

  // Section/Item states
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [targetSectionId, setTargetSectionId] = useState('');

  const fetchForms = async () => {
    try {
      const res = await api.get('/evaluation-forms');
      setForms(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const handleCloneForm = async (id: string) => {
    if (window.confirm('คุณต้องการคัดลอกแบบประเมินนี้ใช่หรือไม่? (โครงสร้างหัวข้อและข้อคำถามทั้งหมดจะถูกคัดลอกด้วย)')) {
      setLoading(true);
      try {
        await api.post(`/evaluation-forms/${id}/clone`);
        fetchForms();
      } catch (err) {
        console.error(err);
        alert('เกิดข้อผิดพลาดในการคัดลอกแบบประเมิน');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenForm = (form: any = null) => {
    if (form) {
      setSelectedForm(form);
      setFormData({
        title: form.title,
        description: form.description || '',
        target_role: form.target_role,
        is_active: form.is_active
      });
    } else {
      setSelectedForm(null);
      setFormData({ title: '', description: '', target_role: 'PROFESSIONAL', is_active: 0 });
    }
    setOpenForm(true);
  };

  const handleOpenDetail = async (form: any) => {
    setLoading(true);
    try {
      const res = await api.get(`/evaluation-forms/${form.id}`);
      setSelectedForm(res.data);
      setOpenDetail(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveForm = async () => {
    try {
      if (selectedForm && !openDetail) {
        await api.put(`/evaluation-forms/${selectedForm.id}`, formData);
      } else {
        await api.post('/evaluation-forms', formData);
      }
      setOpenForm(false);
      fetchForms();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteForm = async (id: string) => {
    if (window.confirm('ยืนยันการลบแบบประเมินนี้? ข้อมูลหัวข้อและข้อคำถามทั้งหมดจะถูกลบด้วย')) {
      try {
        await api.delete(`/evaluation-forms/${id}`);
        fetchForms();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleAddSection = async () => {
      if (!newSectionTitle) return;
      try {
          await api.post('/evaluation-forms/sections', {
              form_id: selectedForm.id,
              title: newSectionTitle,
              order_index: selectedForm.sections?.length || 0
          });
          setNewSectionTitle('');
          handleOpenDetail(selectedForm);
      } catch (err) {
          console.error(err);
      }
  };

  const handleAddItem = async (sectionId: string) => {
      if (!newItemLabel) return;
      try {
          const section = selectedForm.sections.find((s: any) => s.id === sectionId);
          const nextOrder = section?.items?.length || 0;

          await api.post('/evaluation-forms/items', {
              section_id: sectionId,
              label: newItemLabel,
              max_score: 4,
              order_index: nextOrder
          });
          setNewItemLabel('');
          setTargetSectionId('');
          handleOpenDetail(selectedForm);
      } catch (err) {
          console.error(err);
      }
  };

  const handleDeleteSection = async (id: string) => {
      if (window.confirm('ลบหัวข้อนี้?')) {
          await api.delete(`/evaluation-forms/sections/${id}`);
          handleOpenDetail(selectedForm);
      }
  };

  const handleDeleteItem = async (id: string) => {
    await api.delete(`/evaluation-forms/items/${id}`);
    handleOpenDetail(selectedForm);
};

  const getRoleLabel = (role: string) => {
      switch(role) {
          case 'PROFESSIONAL': return 'ด้านวิชาชีพครู';
          case 'SPECIALIST': return 'ด้านวิชาเอก';
          case 'CO_TEACHER': return 'ครูพี่เลี้ยง';
          default: return role;
      }
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">จัดการแบบประเมินสมรรถนะ (Evaluation Forms)</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenForm()}>
          สร้างแบบประเมินใหม่
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>ชื่อแบบประเมิน</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>กลุ่มเป้าหมาย</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>สถานะ</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.map((form: any) => (
              <TableRow key={form.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{form.title}</Typography>
                  <Typography variant="caption" color="textSecondary">{form.description}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={getRoleLabel(form.target_role)} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  {form.is_active ? (
                    <Chip icon={<ActiveIcon />} label="เปิดใช้งาน" color="success" size="small" />
                  ) : (
                    <Chip icon={<InactiveIcon />} label="ปิดใช้งาน" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Tooltip title="คัดลอกแบบประเมิน">
                      <IconButton color="warning" onClick={() => handleCloneForm(form.id)}>
                        <CloneIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="จัดการข้อคำถาม">
                      <IconButton color="info" onClick={() => handleOpenDetail(form)}>
                        <ListIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="แก้ไขข้อมูลพื้นฐาน">
                      <IconButton color="primary" onClick={() => handleOpenForm(form)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="ลบ">
                      <IconButton color="error" onClick={() => handleDeleteForm(form.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {forms.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>ยังไม่มีแบบประเมินในระบบ</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form Metadata Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} fullWidth maxWidth="xs">
        <DialogTitle>{selectedForm ? 'แก้ไขข้อมูลแบบประเมิน' : 'สร้างแบบประเมินใหม่'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="ชื่อแบบประเมิน"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
            required
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="คำอธิบาย"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            fullWidth
            label="กลุ่มเป้าหมาย (รอบการประเมิน)"
            value={formData.target_role}
            onChange={(e) => setFormData({ ...formData, target_role: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="PROFESSIONAL">รอบวิชาชีพครู (Professional)</MenuItem>
            <MenuItem value="SPECIALIST">รอบด้านวิชาเอก (Specialist)</MenuItem>
            <MenuItem value="CO_TEACHER">ครูพี่เลี้ยง (Co-Teacher)</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch 
                checked={formData.is_active === 1} 
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })} 
              />
            }
            label="เปิดใช้งาน (Active)"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenForm(false)}>ยกเลิก</Button>
          <Button onClick={handleSaveForm} variant="contained" color="primary" disabled={!formData.title}>บันทึก</Button>
        </DialogActions>
      </Dialog>

      {/* Form Detail (Sections/Items) Dialog */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>
          จัดการข้อคำถาม: {selectedForm?.title}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
           <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>เพิ่มหัวข้อใหม่ (Section)</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField 
                        size="small" 
                        fullWidth 
                        placeholder="เช่น 1. ด้านการปฏิบัติงาน" 
                        value={newSectionTitle}
                        onChange={(e) => setNewSectionTitle(e.target.value)}
                    />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddSection}>เพิ่มหัวข้อ</Button>
                </Box>
           </Box>

           <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>โครงสร้างแบบประเมินปัจจุบัน</Typography>
           {selectedForm?.sections?.map((section: any) => (
               <Accordion key={section.id} sx={{ mb: 1 }}>
                   <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pr: 2 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>{section.title}</Typography>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                   </AccordionSummary>
                   <AccordionDetails>
                        <List>
                            {section.items?.map((item: any) => (
                                <ListItem 
                                    key={item.id} 
                                    divider
                                    secondaryAction={
                                        <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemText primary={item.label} secondary={`คะแนนเต็ม: ${item.max_score}`} />
                                </ListItem>
                            ))}
                        </List>
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                             <TextField 
                                size="small" 
                                fullWidth 
                                label="ข้อคำถาม/เกณฑ์การประเมิน" 
                                value={targetSectionId === section.id ? newItemLabel : ''}
                                onChange={(e) => {
                                    setTargetSectionId(section.id);
                                    setNewItemLabel(e.target.value);
                                }}
                            />
                            <Button variant="outlined" startIcon={<AddItemIcon />} onClick={() => handleAddItem(section.id)}>เพิ่มข้อคำถาม</Button>
                        </Box>
                   </AccordionDetails>
               </Accordion>
           ))}
           {selectedForm?.sections?.length === 0 && (
               <Typography color="textSecondary" align="center">ยังไม่มีหัวข้อในแบบประเมินนี้</Typography>
           )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetail(false)} variant="contained">ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationFormManager;
